import { useEffect, useRef } from 'react';

/**
 * The app's ONE confirm pattern — themed replacement for window.confirm().
 * Equal-weight soft actions per the ui-polish button rules: confirm is a soft
 * semantic wash (danger for destructive), cancel is ghost. Escape or a click
 * on the backdrop cancels; the confirm button takes focus on open.
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  /**
   * Label for the dismiss button. Defaults to 'Cancel' — but OVERRIDE IT whenever
   * the action itself is called "cancel" (e.g. cancelling an access request).
   * A dialog titled "Cancel this request?" with a button labelled "Cancel" is a
   * trap: the user who WANTS to cancel clicks "Cancel" and silently aborts.
   * That exact confusion is why access-request cancel appeared broken (prod #37).
   */
  cancelLabel?: string;
  /** Destructive action — confirm renders as the soft red wash. */
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div
      className="confirm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="confirm-card" role="dialog" aria-modal="true" aria-label={title}>
        <h3 className="confirm-title">{title}</h3>
        {body && <p className="confirm-body">{body}</p>}
        <div className="btn-row confirm-actions">
          <button
            ref={confirmRef}
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
          <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
